import React from 'react';

import {
  Anchor,
  Badge,
  Button,
  Code,
  CopyButton,
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconCopy, IconTrash } from '@tabler/icons-react';
import KiloActionIcon from 'components/KiloActionIcon';
import KiloTooltip from 'components/KiloTooltip';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import useMCPConnections from 'hooks/aiConnect/useMCPConnections';
import { MCPConnection } from 'types';
import { KILO_PRIVACY_POLICY_URL } from 'utils/external-resources';
import { MCP_CONNECTION_URL } from 'utils/mcp-api-client';

import RevokeConnectionModal from './RevokeConnectionModal';

dayjs.extend(utc);
dayjs.extend(timezone);

interface AIConnectCardProps {
  gymTitle?: string;
  /**
   * Include the short "what this is" blurb and the policy footnote. Turn off where the page
   * already says both - the Integrations → Claude AI page carries full setup instructions
   * above this card.
   */
  withGuidance?: boolean;
}

const AIConnectCard = ({ withGuidance = true }: AIConnectCardProps): JSX.Element => {
  const { session } = useAuth();
  const { data, isLoading } = useMCPConnections();
  const [connectionToRevoke, setConnectionToRevoke] = React.useState<MCPConnection | null>(null);
  const [isRevokeModalOpened, { open: openRevokeModal, close: closeRevokeModal }] = useDisclosure(false);

  const connections = data?.data.results ?? [];

  const timezoneName = session?.gym?.timezone ?? 'UTC';
  const formatTimestamp = (value: string): string =>
    dayjs(value).tz(timezoneName).format('MMM D, YYYY h:mm A');

  const handleRevokeClick = (connection: MCPConnection): void => {
    setConnectionToRevoke(connection);
    openRevokeModal();
  };

  return (
    <>
      {connectionToRevoke && (
        <RevokeConnectionModal
          opened={isRevokeModalOpened}
          onClose={closeRevokeModal}
          connection={connectionToRevoke}
        />
      )}

      <Paper shadow='xl' p='md' pb='lg' sx={{ position: 'relative' }} data-cy='aiConnectCard'>
        <LoadingOverlay visible={isLoading} />
        <Title order={4} mb='sm'>
          AI Connect
        </Title>

        <Stack spacing='md'>
          {withGuidance && (
            <Text>
              Connect Claude to your gym&apos;s analytics. In Claude, add a custom connector using the URL
              below, then approve access for this gym.
            </Text>
          )}

          <div>
            <Text size='xs' color='dimmed' fw={600}>
              MCP CONNECTION URL
            </Text>
            <Group spacing='xs' mt={4} noWrap align='center'>
              <Code sx={{ flex: 1, wordBreak: 'break-all' }} data-cy='aiConnectUrl'>
                {MCP_CONNECTION_URL}
              </Code>
              <CopyButton value={MCP_CONNECTION_URL}>
                {({ copied, copy }) => (
                  <Button
                    variant='outline'
                    data-cy='aiConnectCopyButton'
                    onClick={copy}
                    leftIcon={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </div>

          <Divider mx='-md' />

          <Text fw={500}>Active connections</Text>
          {connections.length === 0 ? (
            <Text color='dimmed' data-cy='aiConnectEmptyState'>
              No AI tools are connected yet.
            </Text>
          ) : (
            <>
              <Paper withBorder radius='md' sx={{ overflowX: 'auto' }}>
                <Table verticalSpacing='sm' horizontalSpacing='md' sx={{ minWidth: 620 }}>
                  <thead>
                    <tr>
                      <th>Connection</th>
                      <th>Access</th>
                      <th>Authorized on</th>
                      <th>Last used</th>
                      {/* Header intentionally blank — the row action is icon-only and labelled. */}
                      <th aria-label='Actions' style={{ width: 72 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {connections.map(connection => (
                      <tr key={connection.id} data-cy='aiConnectConnectionRow'>
                        <td>
                          <Text fw={500} sx={{ whiteSpace: 'nowrap' }}>
                            {connection.name}
                          </Text>
                        </td>
                        <td>
                          <Group spacing={6}>
                            {connection.scopes.map(scope => (
                              <Badge key={scope} variant='light' size='sm'>
                                {scope}
                              </Badge>
                            ))}
                          </Group>
                        </td>
                        <td>
                          <Text color='dimmed' sx={{ whiteSpace: 'nowrap' }}>
                            {formatTimestamp(connection.createdAt)}
                          </Text>
                        </td>
                        <td>
                          <Text color='dimmed' sx={{ whiteSpace: 'nowrap' }}>
                            {connection.lastUsedAt ? formatTimestamp(connection.lastUsedAt) : 'Never'}
                          </Text>
                        </td>
                        <td>
                          <Group position='right' spacing='xs' noWrap>
                            <KiloTooltip label='Revoke'>
                              <KiloActionIcon
                                rootVariantType='danger'
                                data-cy='aiConnectRevokeButton'
                                aria-label={`Revoke ${connection.name}`}
                                onClick={() => handleRevokeClick(connection)}
                              >
                                <IconTrash size={16} />
                              </KiloActionIcon>
                            </KiloTooltip>
                          </Group>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Paper>
              <Text size='xs' color='dimmed'>
                Kilo never displays an AI tool&apos;s access token - the tool holds it. Revoking a connection
                invalidates its token immediately and cannot be undone; the tool has to be connected again.
              </Text>
            </>
          )}

          {withGuidance && (
            <Text size='xs' color='dimmed'>
              Connecting Kilo to an AI provider constitutes acceptance of Kilo&apos;s{' '}
              <Anchor
                href={KILO_PRIVACY_POLICY_URL}
                target='_blank'
                rel='noreferrer'
                size='xs'
                data-cy='aiConnectPolicyLink'
              >
                privacy policy
              </Anchor>
              .
            </Text>
          )}
        </Stack>
      </Paper>
    </>
  );
};

export default AIConnectCard;
